/**
 * Registry of every percentage sub-calculator: its route, nav metadata, SEO
 * copy, the (pure) compute config its widget uses, and its on-page content
 * (how-to + examples + FAQ). One generic page renders any entry, so adding a
 * calculator later is just a new entry here + a thin route file.
 *
 * All user-facing strings are Paraglide `m.*` functions (English authored now,
 * other locales translated as a follow-up).
 */
import { m } from "@/paraglide/messages";
import type { PercentCalcConfig } from "../components/PercentCalc";
import {
  discount,
  percentChange,
  percentDecrease,
  percentDifference,
  tip,
} from "./percentage";

export interface FaqItem {
  q: () => string;
  a: () => string;
}

export interface CalcDef {
  /** Route segment, e.g. "percentage-increase". */
  slug: string;
  /** Full path, e.g. "/percentage-calculator/percentage-increase". */
  path: string;
  /** Emoji for in-tool nav + related links. */
  icon: string;
  navName: () => string;
  metaTitle: () => string;
  metaDescription: () => string;
  h1: () => string;
  intro: () => string;
  config: PercentCalcConfig;
  howTo: () => string;
  examples: Array<() => string>;
  faqs: FaqItem[];
}

const BASE = "/percentage-calculator";

export const CALCULATORS: Record<string, CalcDef> = {
  "percentage-increase": {
    slug: "percentage-increase",
    path: `${BASE}/percentage-increase`,
    icon: "📈",
    navName: () => m.pct_nav_increase(),
    metaTitle: () => m.pct_increase_meta_title(),
    metaDescription: () => m.pct_increase_meta_description(),
    h1: () => m.pct_increase_h1(),
    intro: () => m.pct_increase_intro(),
    config: {
      fields: [
        { id: "old", label: () => m.pct_field_original() },
        { id: "new", label: () => m.pct_field_new() },
      ],
      compute: (v) => {
        const r = percentChange(v.old, v.new);
        return r === null
          ? null
          : [
              {
                label: () => m.pct_result_increase(),
                value: r,
                kind: "percent",
              },
            ];
      },
    },
    howTo: () => m.pct_increase_howto(),
    examples: [
      () => m.pct_increase_ex1(),
      () => m.pct_increase_ex2(),
      () => m.pct_increase_ex3(),
      () => m.pct_increase_ex4(),
    ],
    faqs: [
      { q: () => m.pct_increase_faq1_q(), a: () => m.pct_increase_faq1_a() },
      { q: () => m.pct_increase_faq2_q(), a: () => m.pct_increase_faq2_a() },
      { q: () => m.pct_increase_faq3_q(), a: () => m.pct_increase_faq3_a() },
      { q: () => m.pct_increase_faq4_q(), a: () => m.pct_increase_faq4_a() },
    ],
  },

  "percentage-decrease": {
    slug: "percentage-decrease",
    path: `${BASE}/percentage-decrease`,
    icon: "📉",
    navName: () => m.pct_nav_decrease(),
    metaTitle: () => m.pct_decrease_meta_title(),
    metaDescription: () => m.pct_decrease_meta_description(),
    h1: () => m.pct_decrease_h1(),
    intro: () => m.pct_decrease_intro(),
    config: {
      fields: [
        { id: "old", label: () => m.pct_field_original() },
        { id: "new", label: () => m.pct_field_new() },
      ],
      compute: (v) => {
        const r = percentDecrease(v.old, v.new);
        return r === null
          ? null
          : [
              {
                label: () => m.pct_result_decrease(),
                value: r,
                kind: "percent",
              },
            ];
      },
    },
    howTo: () => m.pct_decrease_howto(),
    examples: [
      () => m.pct_decrease_ex1(),
      () => m.pct_decrease_ex2(),
      () => m.pct_decrease_ex3(),
      () => m.pct_decrease_ex4(),
    ],
    faqs: [
      { q: () => m.pct_decrease_faq1_q(), a: () => m.pct_decrease_faq1_a() },
      { q: () => m.pct_decrease_faq2_q(), a: () => m.pct_decrease_faq2_a() },
      { q: () => m.pct_decrease_faq3_q(), a: () => m.pct_decrease_faq3_a() },
      { q: () => m.pct_decrease_faq4_q(), a: () => m.pct_decrease_faq4_a() },
    ],
  },

  "percentage-change": {
    slug: "percentage-change",
    path: `${BASE}/percentage-change`,
    icon: "🔀",
    navName: () => m.pct_nav_change(),
    metaTitle: () => m.pct_change_meta_title(),
    metaDescription: () => m.pct_change_meta_description(),
    h1: () => m.pct_change_h1(),
    intro: () => m.pct_change_intro(),
    config: {
      fields: [
        { id: "old", label: () => m.pct_field_original() },
        { id: "new", label: () => m.pct_field_new() },
      ],
      compute: (v) => {
        const r = percentChange(v.old, v.new);
        return r === null
          ? null
          : [{ label: () => m.pct_result_change(), value: r, kind: "percent" }];
      },
    },
    howTo: () => m.pct_change_howto(),
    examples: [
      () => m.pct_change_ex1(),
      () => m.pct_change_ex2(),
      () => m.pct_change_ex3(),
      () => m.pct_change_ex4(),
    ],
    faqs: [
      { q: () => m.pct_change_faq1_q(), a: () => m.pct_change_faq1_a() },
      { q: () => m.pct_change_faq2_q(), a: () => m.pct_change_faq2_a() },
      { q: () => m.pct_change_faq3_q(), a: () => m.pct_change_faq3_a() },
      { q: () => m.pct_change_faq4_q(), a: () => m.pct_change_faq4_a() },
    ],
  },

  "percentage-difference": {
    slug: "percentage-difference",
    path: `${BASE}/percentage-difference`,
    icon: "⚖️",
    navName: () => m.pct_nav_difference(),
    metaTitle: () => m.pct_difference_meta_title(),
    metaDescription: () => m.pct_difference_meta_description(),
    h1: () => m.pct_difference_h1(),
    intro: () => m.pct_difference_intro(),
    config: {
      fields: [
        { id: "a", label: () => m.pct_field_first() },
        { id: "b", label: () => m.pct_field_second() },
      ],
      compute: (v) => {
        const r = percentDifference(v.a, v.b);
        return r === null
          ? null
          : [
              {
                label: () => m.pct_result_difference(),
                value: r,
                kind: "percent",
              },
            ];
      },
    },
    howTo: () => m.pct_difference_howto(),
    examples: [
      () => m.pct_difference_ex1(),
      () => m.pct_difference_ex2(),
      () => m.pct_difference_ex3(),
      () => m.pct_difference_ex4(),
    ],
    faqs: [
      {
        q: () => m.pct_difference_faq1_q(),
        a: () => m.pct_difference_faq1_a(),
      },
      {
        q: () => m.pct_difference_faq2_q(),
        a: () => m.pct_difference_faq2_a(),
      },
      {
        q: () => m.pct_difference_faq3_q(),
        a: () => m.pct_difference_faq3_a(),
      },
      {
        q: () => m.pct_difference_faq4_q(),
        a: () => m.pct_difference_faq4_a(),
      },
    ],
  },

  "tip-calculator": {
    slug: "tip-calculator",
    path: `${BASE}/tip-calculator`,
    icon: "🧾",
    navName: () => m.pct_nav_tip(),
    metaTitle: () => m.pct_tip_meta_title(),
    metaDescription: () => m.pct_tip_meta_description(),
    h1: () => m.pct_tip_h1(),
    intro: () => m.pct_tip_intro(),
    config: {
      fields: [
        { id: "bill", label: () => m.pct_field_bill() },
        {
          id: "pct",
          label: () => m.pct_field_tip_percent(),
          defaultValue: "15",
        },
        {
          id: "split",
          label: () => m.pct_field_people(),
          defaultValue: "1",
          min: 1,
        },
      ],
      compute: (v) => {
        const r = tip(v.bill, v.pct, v.split);
        if (r === null) return null;
        return [
          {
            label: () => m.pct_result_total(),
            value: r.total,
            kind: "currency",
          },
          {
            label: () => m.pct_result_tip(),
            value: r.tipAmount,
            kind: "currency",
          },
          {
            label: () => m.pct_result_per_person(),
            value: r.perPerson,
            kind: "currency",
          },
        ];
      },
    },
    howTo: () => m.pct_tip_howto(),
    examples: [
      () => m.pct_tip_ex1(),
      () => m.pct_tip_ex2(),
      () => m.pct_tip_ex3(),
      () => m.pct_tip_ex4(),
    ],
    faqs: [
      { q: () => m.pct_tip_faq1_q(), a: () => m.pct_tip_faq1_a() },
      { q: () => m.pct_tip_faq2_q(), a: () => m.pct_tip_faq2_a() },
      { q: () => m.pct_tip_faq3_q(), a: () => m.pct_tip_faq3_a() },
      { q: () => m.pct_tip_faq4_q(), a: () => m.pct_tip_faq4_a() },
    ],
  },

  "discount-calculator": {
    slug: "discount-calculator",
    path: `${BASE}/discount-calculator`,
    icon: "🏷️",
    navName: () => m.pct_nav_discount(),
    metaTitle: () => m.pct_discount_meta_title(),
    metaDescription: () => m.pct_discount_meta_description(),
    h1: () => m.pct_discount_h1(),
    intro: () => m.pct_discount_intro(),
    config: {
      fields: [
        { id: "price", label: () => m.pct_field_price() },
        { id: "off", label: () => m.pct_field_discount_percent() },
      ],
      compute: (v) => {
        const r = discount(v.price, v.off);
        if (r === null) return null;
        return [
          {
            label: () => m.pct_result_final_price(),
            value: r.finalPrice,
            kind: "currency",
          },
          {
            label: () => m.pct_result_saved(),
            value: r.saved,
            kind: "currency",
          },
        ];
      },
    },
    howTo: () => m.pct_discount_howto(),
    examples: [
      () => m.pct_discount_ex1(),
      () => m.pct_discount_ex2(),
      () => m.pct_discount_ex3(),
      () => m.pct_discount_ex4(),
    ],
    faqs: [
      { q: () => m.pct_discount_faq1_q(), a: () => m.pct_discount_faq1_a() },
      { q: () => m.pct_discount_faq2_q(), a: () => m.pct_discount_faq2_a() },
      { q: () => m.pct_discount_faq3_q(), a: () => m.pct_discount_faq3_a() },
      { q: () => m.pct_discount_faq4_q(), a: () => m.pct_discount_faq4_a() },
    ],
  },
};

export const CALCULATOR_LIST: CalcDef[] = Object.values(CALCULATORS);

/**
 * Safe lookup by slug. Uses `Object.hasOwn` so inherited properties
 * ("constructor", "toString", "__proto__", …) resolve to undefined instead of
 * an Object.prototype member — otherwise /percentage-calculator/constructor
 * would slip past the notFound() guard and crash the page.
 */
export function getCalculator(slug: string): CalcDef | undefined {
  return Object.hasOwn(CALCULATORS, slug) ? CALCULATORS[slug] : undefined;
}
